import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Tag,
    Text,
    VStack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { useState } from 'react';
import { TbCheck, TbGripVertical, TbPencil, TbPlus, TbTrash } from 'react-icons/tb';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    PARTBAY_BLUE,
    PARTBAY_BLUE_TINT,
    SETTINGS_BORDER_COLOR,
    SettingsFlowSplit,
} from '../../../../components/settings/primitives';
import {
    MAX_PRICE_MATCH_ENTRIES,
    describePriceMatchEntry,
    emptyPriceMatchDraft,
    isPriceMatchDraftValid,
    makePriceMatchId,
    priceMatchEntryChips,
    type PriceMatchDraftEntry,
} from '../../../../components/settings/priceMatchCriteria';
import PriceMatchCriteriaFields from './PriceMatchCriteriaFields';

/**
 * The rail the cards hang off, in pixels. Everything that sits on it — the
 * priority badges and the "+" that extends it — is centred against RAIL_CENTER
 * by pulling itself back out of RAIL_INSET, so the line runs through the middle
 * of each circle rather than beside it.
 */
const RAIL_INSET = 28;
const RAIL_LEFT = 13;
const RAIL_WIDTH = 2;
const RAIL_CENTER = RAIL_LEFT + RAIL_WIDTH / 2;
const BADGE_SIZE = 20;
const ADD_BUTTON_SIZE = 24;

/** Negative margin that centres a circle of `size` on the rail. */
const railCenterOffset = (size: number) => `${RAIL_CENTER - size / 2 - RAIL_INSET}px`;

const ordinal = (index: number): string => {
    const position = index + 1;
    const suffix =
        position % 100 >= 11 && position % 100 <= 13
            ? 'th'
            : position % 10 === 1
                ? 'st'
                : position % 10 === 2
                    ? 'nd'
                    : position % 10 === 3
                        ? 'rd'
                        : 'th';
    return `${position}${suffix}`;
};

interface PriceMatchFlowCardProps {
    entry: PriceMatchDraftEntry;
    index: number;
    isExpanded: boolean;
    onToggleExpanded: () => void;
    onChange: (next: PriceMatchDraftEntry) => void;
    onRemove: () => void;
}

/**
 * One card in the order: a draggable step whose criteria can be changed right
 * here rather than only in the creation pane. Collapsed it reads as chips plus
 * the sentence the criteria come to; expanded it is the same fields the card
 * was built with.
 */
const PriceMatchFlowCard = ({
    entry,
    index,
    isExpanded,
    onToggleExpanded,
    onChange,
    onRemove,
}: PriceMatchFlowCardProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: entry.id,
    });
    const chips = priceMatchEntryChips(entry);

    return (
        <Box
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
            position="relative"
            opacity={isDragging ? 0.6 : 1}
            zIndex={isDragging ? 2 : undefined}
        >
            {/* Priority label sits on the rail, the way a flow numbers its steps. */}
            <HStack spacing={2} mb={2} ml={railCenterOffset(BADGE_SIZE)}>
                <Flex
                    align="center"
                    justify="center"
                    boxSize={`${BADGE_SIZE}px`}
                    borderRadius="full"
                    bg={PARTBAY_BLUE_TINT}
                    color={PARTBAY_BLUE}
                    fontSize="0.65rem"
                    fontWeight="bold"
                    flexShrink={0}
                >
                    {index + 1}
                </Flex>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                    {index === 0 ? 'Match first' : `${ordinal(index)} — then`}
                </Text>
            </HStack>

            <Box
                borderWidth="1px"
                borderColor={isExpanded ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
                borderRadius="lg"
                bg="white"
                overflow="hidden"
            >
                <Flex
                    align="center"
                    gap={2}
                    px={3}
                    py={2}
                    bg={isExpanded ? PARTBAY_BLUE_TINT : 'gray.50'}
                    borderBottomWidth="1px"
                    borderColor={isExpanded ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
                >
                    <Box
                        {...attributes}
                        {...listeners}
                        cursor="grab"
                        color="gray.400"
                        _hover={{ color: 'gray.600' }}
                        display="flex"
                        alignItems="center"
                        aria-label="Reorder card"
                    >
                        <Icon as={TbGripVertical as React.ElementType} boxSize={4} />
                    </Box>
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color={isExpanded ? PARTBAY_BLUE : 'gray.600'}
                        textTransform="uppercase"
                        letterSpacing="wide"
                    >
                        Price match to
                    </Text>
                    <Box flex={1} />
                    <IconButton
                        aria-label={isExpanded ? 'Done editing card' : 'Edit card'}
                        icon={
                            <Icon
                                as={(isExpanded ? TbCheck : TbPencil) as React.ElementType}
                                boxSize={4}
                            />
                        }
                        size="xs"
                        variant="ghost"
                        onClick={onToggleExpanded}
                    />
                    <IconButton
                        aria-label="Remove card"
                        icon={<Icon as={TbTrash as React.ElementType} boxSize={4} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={onRemove}
                    />
                </Flex>

                <Box px={4} py={3}>
                    {isExpanded ? (
                        <PriceMatchCriteriaFields value={entry} onChange={onChange} isCompact />
                    ) : (
                        <VStack align="stretch" spacing={2}>
                            {chips.length > 0 ? (
                                <Wrap spacing={2}>
                                    {chips.map((chip, chipIndex) => (
                                        <WrapItem key={`${chip.label}-${chipIndex}`}>
                                            <Tag
                                                size="sm"
                                                variant="subtle"
                                                colorScheme={chip.colorScheme}
                                            >
                                                {chip.label}
                                            </Tag>
                                        </WrapItem>
                                    ))}
                                </Wrap>
                            ) : (
                                <Text fontSize="sm" color="red.500">
                                    No criteria on this card yet.
                                </Text>
                            )}
                            <Text fontSize="sm" color="gray.600">
                                {describePriceMatchEntry(entry)}
                            </Text>
                        </VStack>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export interface PriceMatchOrderEditorProps {
    entries: Array<PriceMatchDraftEntry>;
    onChange: (next: Array<PriceMatchDraftEntry>) => void;
    /** Heading over the creation pane. */
    leftTitle?: string;
    rightTitle?: string;
    rightDescription?: string;
}

/**
 * The price-match order, split: how a card is built on the left, the order the
 * cards are tried in on the right.
 *
 * The right pane is the order itself — a flow of cards down a rail, top card
 * first — so reordering is dragging one above another, and a card's criteria can
 * be changed in place instead of reopening the creation pane. Cards are ranked,
 * not filtered: PartBay walks them from the top and price matches to the most
 * cost-effective option that meets the first card it can satisfy.
 */
const PriceMatchOrderEditor = ({
    entries,
    onChange,
    leftTitle = 'Create a card',
    rightTitle = 'Price match order',
    rightDescription = 'Tried top to bottom. Drag a card to change its priority, or edit it here.',
}: PriceMatchOrderEditorProps) => {
    const [newCard, setNewCard] = useState<PriceMatchDraftEntry>(emptyPriceMatchDraft);
    // The card whose fields are open on the right; only one at a time.
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const isFull = entries.length >= MAX_PRICE_MATCH_ENTRIES;
    const canAdd = isPriceMatchDraftValid(newCard) && !isFull;

    const handleAddCard = () => {
        if (!canAdd) return;
        onChange([...entries, { ...newCard, id: makePriceMatchId() }]);
        setNewCard(emptyPriceMatchDraft());
        setExpandedId(null);
    };

    /** The "+" under the rail: a blank card, opened for editing where it lands. */
    const handleAddBlankCard = () => {
        if (isFull) return;
        const blank = emptyPriceMatchDraft();
        onChange([...entries, blank]);
        setExpandedId(blank.id);
    };

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return;
        const from = entries.findIndex((entry) => entry.id === active.id);
        const to = entries.findIndex((entry) => entry.id === over.id);
        if (from === -1 || to === -1) return;
        onChange(arrayMove(entries, from, to));
    };

    const left = (
        <VStack align="stretch" spacing={5}>
            {/* Stated up front, and in a callout, because a card is only
                understandable once the "at least one criteria" rule is. */}
            <Alert status="info" borderRadius="md" alignItems="flex-start">
                <AlertIcon mt="2px" />
                <AlertDescription fontSize="sm">
                    Choose the criteria you would like to price match to, which includes the vendor,
                    part type, and pricing source. You need to choose at least one criteria, and it
                    will create a card that finds the most cost effective option meeting the criteria
                    you specified.
                </AlertDescription>
            </Alert>

            <PriceMatchCriteriaFields value={newCard} onChange={setNewCard} />

            <Box
                borderWidth="1px"
                borderColor={canAdd ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
                bg={canAdd ? PARTBAY_BLUE_TINT : 'gray.50'}
                borderRadius="md"
                px={4}
                py={3}
            >
                <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                    Rule summary
                </Text>
                <Text fontSize="sm" color={canAdd ? 'gray.800' : 'gray.500'}>
                    {isPriceMatchDraftValid(newCard)
                        ? describePriceMatchEntry(newCard)
                        : 'Pick a vendor, a part type or a marketplace to see what this card will do.'}
                </Text>
            </Box>

            <HStack spacing={3}>
                <Button
                    size="sm"
                    bg={PARTBAY_BLUE}
                    color="white"
                    _hover={{ bg: '#005BC4' }}
                    leftIcon={<Icon as={TbPlus as React.ElementType} boxSize={4} />}
                    onClick={handleAddCard}
                    isDisabled={!canAdd}
                >
                    Add Card
                </Button>
                {isFull && (
                    <Text fontSize="xs" color="gray.500">
                        Maximum of {MAX_PRICE_MATCH_ENTRIES} cards reached.
                    </Text>
                )}
            </HStack>
        </VStack>
    );

    const right =
        entries.length === 0 ? (
            <Box
                borderWidth="1px"
                borderStyle="dashed"
                borderColor={SETTINGS_BORDER_COLOR}
                borderRadius="lg"
                px={6}
                py={10}
                textAlign="center"
                bg="white"
            >
                <Text fontSize="sm" color="gray.500">
                    No cards yet. Build one on the left and it will appear here.
                </Text>
            </Box>
        ) : (
            <Box position="relative" pl={`${RAIL_INSET}px`}>
                {/* The rail the cards hang off: the order, read top to bottom. */}
                <Box
                    position="absolute"
                    left={`${RAIL_LEFT}px`}
                    // Runs from the first badge's centre to the "+" that ends
                    // it, so the rail reads as one path.
                    top={`${BADGE_SIZE / 2}px`}
                    bottom={`${ADD_BUTTON_SIZE / 2}px`}
                    w={`${RAIL_WIDTH}px`}
                    bg={PARTBAY_BLUE}
                    opacity={0.35}
                />
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={entries.map((entry) => entry.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <VStack align="stretch" spacing={5}>
                            {entries.map((entry, index) => (
                                <PriceMatchFlowCard
                                    key={entry.id}
                                    entry={entry}
                                    index={index}
                                    isExpanded={expandedId === entry.id}
                                    onToggleExpanded={() =>
                                        setExpandedId(expandedId === entry.id ? null : entry.id)
                                    }
                                    onChange={(next) =>
                                        onChange(
                                            entries.map((candidate) =>
                                                candidate.id === entry.id ? next : candidate,
                                            ),
                                        )
                                    }
                                    onRemove={() => {
                                        onChange(
                                            entries.filter((candidate) => candidate.id !== entry.id),
                                        );
                                        if (expandedId === entry.id) setExpandedId(null);
                                    }}
                                />
                            ))}
                        </VStack>
                    </SortableContext>
                </DndContext>

                <IconButton
                    aria-label="Add a card"
                    icon={<Icon as={TbPlus as React.ElementType} boxSize={4} />}
                    size="xs"
                    variant="outline"
                    bg="white"
                    boxSize={`${ADD_BUTTON_SIZE}px`}
                    minW={`${ADD_BUTTON_SIZE}px`}
                    mt={4}
                    ml={railCenterOffset(ADD_BUTTON_SIZE)}
                    borderRadius="full"
                    onClick={handleAddBlankCard}
                    isDisabled={isFull}
                />
            </Box>
        );

    return (
        <SettingsFlowSplit
            leftWidth="45%"
            leftTitle={leftTitle}
            left={left}
            rightTitle={rightTitle}
            rightDescription={rightDescription}
            right={right}
        />
    );
};

export default PriceMatchOrderEditor;
